import { useEffect, useState, useCallback } from "react";
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import API from 'services/API';
import { LoadingButton } from '@mui/lab';
import { Alert, Divider, IconButton, InputAdornment, Stack, Typography, Button } from '@mui/material';
// routes
// auth
import { useAuthContext } from '../../auth/useAuthContext';
// components
import FormProvider, { RHFTextField } from '../../components/hook-form';
import Iconify from '../../components/iconify';
import { Accounts } from './Accounts';

// ----------------------------------------------------------------------

export default function AuthLoginForm() {
  const { login, selectAccount, sendOTP, loginAccount } = useAuthContext();

  const [showPassword, setShowPassword] = useState(false);
  const [isSendOtp, setIsSendOtp] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileNumberErr, setMobileNumberErr] = useState('');
  const [mobileNumberOtpSuccess, setMobileNumberOtpSuccess] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [userToken, setUserToken] = useState(null);
  const LoginSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
  });

  const MobileSchema = Yup.object().shape({
    // otp: Yup.string().required('OTP is required'),
  });

  const defaultValues = {
    username: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const mobileMethods = useForm({
    resolver: yupResolver(MobileSchema),
    defaultValues: { otp: '' },
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;
  const { watch } = mobileMethods;
  const values = watch();
  const {
    reset: mobileReset,
    setError: mobileSetError,
    handleSubmit: mobileHandleSubmit,
    formState: { errors: mobileErrors, isSubmitting: mobileIsSubmitting, isSubmitSuccessful: mobileIsSubmitSuccessful },
  } = mobileMethods;
 const getUser = useCallback(async () => {
  try {
    // WB-PP-08: accept token from hash (preferred) or query (legacy)
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const queryParams = new URLSearchParams(window.location.search);
    const token = hashParams.get('token') || queryParams.get('token');

    if (!token) return;

    // Strip token from URL immediately
    const cleanUrl = `${window.location.origin}${window.location.pathname}`;
    window.history.replaceState({}, document.title, cleanUrl);

    const url = `/project-panel/auth/gmail/login/success?token=${encodeURIComponent(token)}`;
    const response = await API.get(url);
    const resData = response?.data;

    if (resData?.data?.user) {
      try {
        await loginAccount(
          resData?.data?.user,
          resData?.data?.token
        );
      } catch (error) {
        console.error(error);
        reset();
        setError('afterSubmit', {
          type: 'manual',
          message: error.response?.data?.message || 'Something went wrong',
        });
      }
    } else if (resData?.data) {
      setAccounts(resData?.data?.websites || []);
      setUserToken(resData?.data?.token || null);
    }
  } catch (err) {
    console.error(err);
  }
}, [loginAccount, reset, setError]);

  useEffect(() => {
    getUser();
  }, [getUser]);
  const onSubmit = async (data) => {
    try {
      setMobileNumberErr("");
      const response = await login(data.username, data.password);
      if (response) {
        if (response?.user) {
          try {

            // await selectAccount(response?.user?.websiteProjectId, response?.token);
          } catch (error) {
            console.error(error);
            reset();
            setError('afterSubmit', {
              ...error,
              message: error.response?.data?.message || 'Something went wrong',
            });
          }
        } else {
          setAccounts(response?.websites || []);
          setUserToken(response?.token || null);
        }
      }
    } catch (error) {
      console.error(error);
      reset();
      setError('afterSubmit', {
        ...error,
        message: error.response?.data?.message || 'Something went wrong',
      });
    }
  };

  const onMobileSubmit = async (data) => {
    try {
      if (!values?.otp) {
        setMobileNumberOtpSuccess("Please enter OTP.");
        return;
      }

      const url = '/project-panel/auth/login-staff-verify-otp';

      const response = await API.post(url, {
        mobileNumber,
        otp: values.otp,
      });

      const resData = response?.data;
      // If direct user object exists
      if (resData?.data?.user) {
        try {
          await loginAccount(
            resData?.data?.user,
            resData?.data?.token
          );
        } catch (error) {
          console.error(error);
          reset();
          setError('afterSubmit', {
            type: 'manual',
            message: error.response?.data?.message || 'Something went wrong',
          });
        }
      }
      // If multiple accounts
      else if (resData?.status) {
        setAccounts(resData?.data?.websites || []);
        setUserToken(resData?.data?.token || null);
      }
      // OTP invalid or backend error
      else {
        setMobileNumberOtpSuccess(resData?.message || 'Invalid OTP.');
      }

    } catch (error) {
      console.error(error);
      mobileReset();
      mobileSetError('afterSubmit', {
        type: 'manual',
        message: error.response?.data?.message || 'Something went wrong',
      });
    }
  };


  const handleGoogleLogin = async () => {
    setMobileNumberErr("");
    // WB-PP-09: path-only allowlisted redirect (not full window.location.href)
    const redirectUrl = encodeURIComponent(`${window.location.origin}/auth/login`);
    // WB-PP-07: env API base only — no hardcoded production host fallback
    const apiBase =
      process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_HOST_API_KEY;
    if (!apiBase) {
      setMobileNumberErr("API base URL is not configured (REACT_APP_API_BASE_URL).");
      return;
    }
    window.open(
      `${apiBase}/project-panel/auth/gmail/login?redirectUrl=${redirectUrl}`,
      "_self"
    );
  };
  const handleSendOtp = async () => {
    try {
      const mobile = values?.mobile;
      if (!mobile || mobile === "") {
        setMobileNumberErr("Please enter mobile number");
      } else {
        setMobileNumberErr("");
        const url = '/project-panel/auth/login-staff-send-otp';
        const response = await API.post(url, {
          mobileNumber: mobile,  // <--- send in body
        });
        if (response?.data?.status) {
          setIsSendOtp(true);
          setMobileNumber(response?.data?.mobileNumber);
          setMobileNumberOtpSuccess(`OTP send to ${response?.data?.mobileNumber}`)
        } else {
          setMobileNumberErr(response?.data?.message);
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  const handleSelectAccount = async (accid) => {
    try {
      await selectAccount(accid);
    } catch (error) {
      console.error(error);
      reset();
      setError('afterSubmit', {
        ...error,
        message: error.response?.data?.message || 'Something went wrong',
      });
    }
  }
  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {accounts && accounts?.length > 0 ?
        <Stack sx={{ mt: 0 }} spacing={0}>
          <Typography variant="h6">Select Account</Typography>
          <Stack sx={{ mt: 2 }} spacing={3}>
            {accounts?.map((account) =>
              <Accounts account={account} onSelectAccount={() => handleSelectAccount(account?._id)} />
            )}
          </Stack>

        </Stack>
        :
        <Stack sx={{ mt: 0 }} spacing={0}>
          <Typography variant="h6">Sign in to Website Builder Project Panel</Typography>
          <Stack sx={{ mt: 2 }} spacing={3}>
            {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
            <RHFTextField name="username" label="Email address" />

            <RHFTextField
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <LoadingButton
            fullWidth
            color="inherit"
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitSuccessful || isSubmitting}
            sx={{
              mt: 3,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
                color: 'primary.contrastText',
              },
            }}
          >
            Login
          </LoadingButton>

          <Divider sx={{ my: 3, borderStyle: 'dashed' }}>or</Divider>

          {/* Continue with Google Button - Styled like in the attached image */}
          <Button
            fullWidth
            size="large"
            startIcon={<Iconify icon="logos:google-icon" sx={{ color: 'rgb(66, 133, 244)' }} />}
            onClick={handleGoogleLogin}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              '&:hover': {
                border: '1px solid #e0e0e0',
                bgcolor: 'action.hover',
              },
            }}
          >
            Continue with Gmail
          </Button>

          <Divider sx={{ my: 2, borderStyle: 'dashed' }}>or</Divider>


          {/* <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>Sign in with Mobile</Typography>
      <Typography variant="subtitle1" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1 }}>
        Finding Easy!
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
        Please Enter Your Mobile Number.
      </Typography> */}

          <FormProvider methods={mobileMethods} onSubmit={mobileHandleSubmit(onMobileSubmit)}>
            <Stack spacing={3}>
              {!!mobileErrors.afterSubmit && <Alert severity="error">{mobileErrors.afterSubmit.message}</Alert>}
              {isSendOtp ? <>
                <RHFTextField
                  name="otp"
                  label="Enter OTP"
                  type="number"
                />
                <small style={{ color: "#FF5630", marginTop: "9px", marginLeft: "8px" }}>{mobileNumberOtpSuccess}</small>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={onMobileSubmit}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  Verify Otp
                </Button>
              </> : <>
                <RHFTextField
                  name="mobile"
                  label="Enter your Mobile number"
                  type="tel"
                />
                <small style={{ color: "#FF5630", marginTop: "9px", marginLeft: "8px" }}>{mobileNumberErr}</small>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handleSendOtp}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  Send Otp
                </Button></>}
              {/* <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={mobileIsSubmitSuccessful || mobileIsSubmitting}
            sx={{
              bgcolor: 'purple.500',
              color: 'common.white',
              '&:hover': {
                bgcolor: 'purple.600',
              },
            }}
          >
            Continue with OTP
          </LoadingButton>  */}
            </Stack>
          </FormProvider>
        </Stack>}
    </FormProvider>
  );
}